import { useState } from 'react';
import { toast } from 'sonner';
import type { ClientTelemetryDay } from '@siliconx/shared';
import { ConditionBadge } from '@siliconx/shared/ui/ConditionBadge';
import { formatDate } from '../../../lib/format';
import { specRows } from '../../../lib/specs';
import { specGlyphFor, type SpecGlyphCtx } from '../spec-glyphs';
import { Card, CardLabel, stripMfrPrefix } from '../shared';
import { MaskedSerial } from '../../asset-detail/MaskedSerial';

/**
 * SIL-565: Overview tab (formerly the 'info' panel) — identity, seller
 * details, location/team, specs, and attachments. Split out of
 * AssetDetailV2.tsx so the route's largest file no longer carries every
 * tab's rendering logic in one module. Content unchanged from the prior
 * inline render — no new data, no new figures.
 */

// ---------------------------------------------------------------------------
// SKU / manufacturer card
// ---------------------------------------------------------------------------

function Chip({ children, primary = false }: { children: React.ReactNode; primary?: boolean }) {
  return (
    <span
      className={
        primary
          ? 'whitespace-nowrap rounded border border-brand-300 bg-brand-50 px-2 py-0.5 text-xs font-semibold text-brand-700'
          : 'whitespace-nowrap rounded border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs text-zinc-700'
      }
    >
      {children}
    </span>
  );
}

function ChevronSep() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-zinc-400">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

/**
 * Logo-only plate (100×44) for the SKU card — the name renders in the chip
 * chain, so this must NOT reuse ManufacturerLogo (which always appends the
 * name text). Literal white background, not `bg-white`: the theme remaps
 * zinc/white tokens to dark under `.dark` (see ManufacturerLogo.tsx).
 * Missing SVG → the plate collapses entirely.
 */
function LogoPlate({ manufacturerId }: { manufacturerId: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <div
      className="flex h-11 w-[100px] flex-none items-center justify-center rounded-md border p-1.5"
      style={{ backgroundColor: '#ffffff', borderColor: '#d4d4d8' }}
    >
      <img
        src={`/mfr-logos/${manufacturerId}.svg`}
        alt=""
        aria-hidden
        onError={() => setFailed(true)}
        className="max-h-full max-w-full object-contain"
      />
    </div>
  );
}

function SkuManufacturerCard({
  manufacturerId,
  manufacturerName,
  modelName,
  generation,
  subClass,
  skuLabel,
  condition,
  gradedAt,
}: {
  manufacturerId: string | null;
  manufacturerName: string | null;
  modelName: string | null;
  generation: string | null;
  subClass: string | null;
  skuLabel: string | null;
  condition: string | null;
  gradedAt: string | null;
}) {
  const chain = [manufacturerName, stripMfrPrefix(modelName, manufacturerName)].filter(
    Boolean,
  ) as string[];
  const tail = [generation, subClass].filter(Boolean).join(' · ');
  return (
    <Card>
      <div className="flex items-center gap-3.5">
        {manufacturerId && <LogoPlate manufacturerId={manufacturerId} />}
        <div>
          <CardLabel>Model</CardLabel>
          {skuLabel && (
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(skuLabel).then(() => toast.success('Copied'));
              }}
              className="mt-1 font-mono text-[13px] text-zinc-500 hover:text-zinc-700"
              title="Copy"
            >
              {skuLabel} ⧉
            </button>
          )}
        </div>
      </div>
      <div className="mt-3.5 flex flex-wrap items-center gap-1">
        {chain.map((c, i) => (
          <span key={c} className="flex items-center gap-1">
            {i > 0 && <ChevronSep />}
            <Chip>{c}</Chip>
          </span>
        ))}
        {tail && (
          <span className="flex items-center gap-1">
            {chain.length > 0 && <ChevronSep />}
            <Chip primary>{tail}</Chip>
          </span>
        )}
        {chain.length === 0 && !tail && <span className="text-sm text-zinc-400">No SKU linked</span>}
      </div>
      <div className="mt-4 flex items-center gap-2 border-t border-zinc-100 pt-3.5">
        <span className="text-xs uppercase tracking-widest text-zinc-500">Condition</span>
        <ConditionBadge value={condition} />
        {gradedAt && (
          <span className="text-xs text-zinc-500">graded from telemetry · {formatDate(gradedAt)}</span>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Specs card
// ---------------------------------------------------------------------------

// Per-spec glyphs/icons come from the SPEC_GLYPHS registry (spec-glyphs.tsx)
// — config-driven so new spec keys only need a registry entry, not card edits.
function SpecsCard({
  assetClassId,
  specFacts,
  specDisplaySchema,
  assetId,
  hasSerial,
  avgPowerDrawW,
}: {
  assetId: string;
  hasSerial: boolean;
  assetClassId: string;
  specFacts: Record<string, string | number | boolean>;
  specDisplaySchema: unknown;
  avgPowerDrawW: number | null;
}) {
  const rows = specRows(assetClassId, specFacts, specDisplaySchema);
  const ctx: SpecGlyphCtx = { avgPowerDrawW };
  return (
    <Card>
      <div className="mb-4 flex items-baseline justify-between">
        <CardLabel>Specs</CardLabel>
        <MaskedSerial assetId={assetId} hasSerial={hasSerial} className="font-mono text-[13px] text-zinc-500" />
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-zinc-400">No spec data available.</p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {rows.map((r) => {
            const entry = specGlyphFor(r.key);
            const value = r.value ?? '';
            const hasValue = value !== '' && value !== '—';
            const glyph = hasValue && entry.glyph ? entry.glyph(value, ctx) : null;
            const Icon = entry.icon;
            return (
              <div key={r.key ?? r.label} className="rounded-md bg-zinc-50 p-3">
                <div className="text-xs uppercase tracking-widest text-zinc-500">
                  {r.label}
                </div>
                {hasValue ? (
                  <>
                    <div className="mt-2 flex items-center gap-2.5">
                      {glyph ??
                        (Icon ? <Icon className="size-4 flex-none text-zinc-400" aria-hidden /> : null)}
                      <span className="font-mono text-base font-semibold text-zinc-900">
                        {value}
                      </span>
                    </div>
                    {entry.sub?.(value, ctx)}
                  </>
                ) : (
                  <>
                    <div className="mt-2 flex items-center gap-2.5">
                      {Icon ? <Icon className="size-4 flex-none text-zinc-300" aria-hidden /> : null}
                      <span className="font-mono text-base text-zinc-400">—</span>
                    </div>
                    <div className="mt-0.5 text-xs text-zinc-400">Not recorded</div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Tab assembly
// ---------------------------------------------------------------------------

export interface OverviewTabProps {
  asset: {
    id: string;
    hasSerial: boolean;
    assetClassId: string;
    condition: string | null;
    manufacturerId: string | null;
    manufacturerName: string | null;
    modelName: string | null;
    generation: string | null;
    subClass: string | null;
    specFacts: Record<string, string | number | boolean>;
    specDisplaySchema: unknown;
  };
  gradedAt: string | null;
  telemetryRecent: ClientTelemetryDay[];
  sellerDetails?: React.ReactNode;
  locationTeam?: React.ReactNode;
  attachments?: React.ReactNode;
}

export function OverviewTab({
  asset,
  gradedAt,
  telemetryRecent,
  sellerDetails,
  locationTeam,
  attachments,
}: OverviewTabProps) {
  return (
    <>
      {/* items-start: the identity card is shorter than the two record cards
          beside it — stretching it left ~80px of dead panel under the grade. */}
      <div className="grid grid-cols-1 items-start gap-3 lg:grid-cols-2">
        <SkuManufacturerCard
          manufacturerId={asset.manufacturerId}
          manufacturerName={asset.manufacturerName}
          modelName={asset.modelName}
          generation={asset.generation}
          subClass={asset.subClass}
          skuLabel={
            asset.modelName
              ? `${stripMfrPrefix(asset.modelName, asset.manufacturerName)}${asset.generation ? ` ${asset.generation}` : ''}`
              : null
          }
          condition={asset.condition}
          gradedAt={gradedAt}
        />
        <div className="flex flex-col gap-3">
          {sellerDetails}
          {locationTeam}
        </div>
      </div>
      <SpecsCard
        assetClassId={asset.assetClassId}
        specFacts={asset.specFacts}
        specDisplaySchema={asset.specDisplaySchema}
        assetId={asset.id}
        hasSerial={asset.hasSerial}
        avgPowerDrawW={
          [...telemetryRecent]
            .sort((a, b) => b.day.localeCompare(a.day))
            .find((d) => d.avgPowerDrawWatts != null)?.avgPowerDrawWatts ?? null
        }
      />
      {attachments}
    </>
  );
}
